import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React from 'react';

import { type Card } from '@/types/productSelectionAndCheckout/types';

import { ProductOptionsSelector } from '../../../productOptionsSelector';
import { IngredientsAccordion } from '../IngredientsAccordion';
import { ModalImage } from '../ModalImage';
import { ModalPricingVolumeAndProductOptionSelector } from '../ModalPricingVolumeAndProductOptionSelector';
import { ModalRatingAndNumReviews } from '../ModalRatingAndNumReviews';

export const LipBalmModalContent = ({ modalCard }: { modalCard: Card }) => {
  const { modal } = modalCard;

  const isMintOriginalPassionFruitOrVanilla = [
    'mint',
    'original',
    'passionFruit',
    'vanilla',
  ].includes(modal.selectableOptionType as string);

  const isSpf30 = modal.selectableOptionType === 'spf30';

  return (
    <>
      <ModalImage modal={modal} />
      <Typography className="cur-mt-4" size="h2">
        {modalCard.modal.title}
      </Typography>
      <div className="cur-mt-4">
        <ProductOptionsSelector
          currentFullSizeItemId={modalCard.fullSizeItemId}
          selectableOptions={modalCard.selectableOptions}
        />
      </div>
      <Typography className="cur-mt-4">{modal.tagline}</Typography>
      <ModalPricingVolumeAndProductOptionSelector modalCard={modalCard} />
      {modalCard.recommendationText && (
        <Highlight className="cur-mt-4" state="success">
          <div
            dangerouslySetInnerHTML={{
              __html: modalCard.recommendationText.modalCallout,
            }}
          ></div>
        </Highlight>
      )}
      <ModalRatingAndNumReviews modalCard={modalCard} />
      <Typography className="cur-mt-4" size="h3">
        Why it&apos;s special
      </Typography>
      <Typography className="cur-mt-4">
        Our dermatologists set out to take the drying and pore-clogging
        ingredients, grease, and—yes—caps out of lip balm. (Let&apos;s be
        honest: those little caps are magnets for dirt and bacteria.)
      </Typography>
      <Typography className="cur-mt-4">
        The lip balm by Curology is designed to be effortless, with our
        deeply-moisturizing, truly vegan formula and radically simple,
        one-handed applicator. Crafted from nourishing oils and natural butters,
        the lip balm locks in lasting moisture, without the need for frequent
        reapplication. Just push down on the slider with your thumb, and apply
        to soak in one of our unique, natural flavors. That&apos;s it! Finally,
        a lip balm that does it all.
      </Typography>
      <Typography className="cur-mt-4" size="h3">
        How to use it
      </Typography>
      <Typography className="cur-mt-4">
        Pull down on the slider with your thumb to open and apply generously to
        lips. Swipe back up to securely close.
      </Typography>
      {isMintOriginalPassionFruitOrVanilla && (
        <>
          <Typography className="cur-mt-4" size="h3">
            Featured ingredients
          </Typography>
          <IngredientsAccordion
            body="Seals in moisture to soften and smooth skin"
            title="Shea butter"
          />
          <IngredientsAccordion
            body="Deeply moisturizes lips without clogging pores or leaving a
            greasy after-feel"
            title="Jojoba seed oil"
          />
          <IngredientsAccordion
            body="Contributes to the lip balm's silken, buttery texture and
            hydrated finish"
            title="Carnauba wax"
          />
          <Typography className="cur-mt-4" size="h3">
            Full ingredients list
          </Typography>
          {modal.selectableOptionType === 'mint' && (
            <Typography className="cur-mt-4">
              Simmondsia Chinensis (Jojoba) Seed Oil, Synthetic Beeswax, Ricinus
              Communis (Castor) Seed Oil, Caprylic/Capric Triglyceride,
              Butyrospermum Parkii (Shea) Butter, Hydrogenated Coco-Glycerides,
              Copernicia Cerifera (Carnauba) Wax, Flavor, Sucrose Tetrastearate
              Triacetate, Sodium Hyaluronate, Tocopheryl Acetate,
              Phenoxyethanol, Rebaudioside A, D-Limonene
            </Typography>
          )}
          {modal.selectableOptionType === 'original' && (
            <Typography className="cur-mt-4">
              Simmondsia Chinensis (Jojoba) Seed Oil, Synthetic Beeswax, Ricinus
              Communis (Castor) Seed Oil, Caprylic/Capric Triglyceride,
              Hydrogenated Coco-Glycerides, Butyrospermum Parkii (Shea) Butter,
              Copernicia Cerifera (Carnauba) Wax, Sucrose Tetrastearate
              Triacetate, Sodium Hyaluronate, Tocopheryl Acetate, Phenoxyethanol
            </Typography>
          )}
          {modal.selectableOptionType === 'passionFruit' && (
            <Typography className="cur-mt-4">
              Simmondsia Chinensis (Jojoba) Seed Oil, Synthetic Beeswax, Ricinus
              Communis (Castor) Seed Oil, Caprylic/Capric Triglyceride,
              Hydrogenated Coco-Glycerides, Butyrospermum Parkii (Shea) Butter,
              Copernicia Cerifera (Carnauba) Wax, Sucrose Tetrastearate
              Triacetate, Sodium Hyaluronate, Tocopheryl Acetate,
              Phenoxyethanol, Rebaudioside A, Flavor, D-Limonene, Linalool
            </Typography>
          )}
          {modal.selectableOptionType === 'vanilla' && (
            <Typography className="cur-mt-4">
              Simmondsia Chinensis (Jojoba) Seed Oil, Synthetic Beeswax, Ricinus
              Communis (Castor) Seed Oil, Caprylic/Capric Triglyceride,
              Hydrogenated Coco-Glycerides, Butyrospermum Parkii (Shea) Butter,
              Copernicia Cerifera (Carnauba) Wax, Sucrose Tetrastearate
              Triacetate, Sodium Hyaluronate, Tocopheryl Acetate,
              Phenoxyethanol, Rebaudioside A, Flavor
            </Typography>
          )}
        </>
      )}
      {isSpf30 && (
        <>
          <Typography className="cur-mt-4" size="h3">
            Active ingredients
          </Typography>
          <IngredientsAccordion
            body="Protects against both UVA and UVB rays"
            title="Zinc oxide 9.4%"
          />
          <Typography className="cur-mt-4">
            The lip balm (SPF 30) cannot prevent all harm from UV rays.
          </Typography>
          <Typography className="cur-mt-4" size="h3">
            Inactive ingredients
          </Typography>
          <Typography className="cur-mt-4">
            Diisostearyl Dimer Dilinoleate, Synthetic Beeswax, Euphorbia
            Cerifera (Candelilla) Wax, Triisostearin, Helianthus annuus
            (Sunflower) Seed Oil, Octyldodecyl Neopentanoate, C12-15 Alkyl
            Benzoate, Phytosteryl/Octyldodecyl Lauroyl Glutamate, Ozokerite,
            Ricinus Communis (Castor) Seed Oil, Jojoba Esters, Butyrospermum
            Parkii (Shea) Butter, Sucrose Tetrastearate Triacetate,
            Caprylic/Capric Triglyceride, Tocopherol, Tocopheryl Acetate,
            Caprylyl Glycol, 1,2-Hexanediol, Triethoxycaprylylsilane, Ethyl
            Ferulate
          </Typography>
          <Typography className="cur-mt-4">
            The lip balm (SPF 30) cannot prevent all harm from UV rays.
          </Typography>
        </>
      )}
    </>
  );
};
